import { useEffect, useSyncExternalStore } from "react";
import { onSessionEnd } from "@/auth/session";
import { useAuth } from "@/auth/useAuth";
import { listPendingUsers } from "../api/adminUsers.api";
import { createPendingApprovals, POLL_MS } from "../lib/pending-approvals";

/** The shared count of accounts waiting for approval (see pending-approvals.ts). */
export const pendingApprovals = createPendingApprovals({
  load: async () => (await listPendingUsers()).users.length,
});

onSessionEnd(() => pendingApprovals.reset());

/**
 * Accounts waiting for approval, for the admin badges. Platform admins only:
 * everybody else gets 0 and the API is not asked.
 */
export function usePendingApprovals(): number {
  const { isPlatformAdmin } = useAuth();
  const count = useSyncExternalStore(pendingApprovals.subscribe, pendingApprovals.getCount);

  useEffect(() => {
    if (!isPlatformAdmin) return;
    void pendingApprovals.refresh();
    const timer = setInterval(() => void pendingApprovals.refresh(), POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void pendingApprovals.refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isPlatformAdmin]);

  return isPlatformAdmin ? count : 0;
}
